import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { events } from "@/mocks/events";
import { formatDate, statusStyleMap } from "@/utils/content";
import EvidenceList from "./components/EvidenceList";
import TimelineSection from "./components/TimelineSection";
import EventSidebar from "./components/EventSidebar";

export default function EventDetail() {
  const { id } = useParams();
  const event = events.find((item) => item.id === id);

  if (!event) {
    return (
      <>
        <Seo
          title="事件未找到 · 131吃瓜"
          description="该事件档案暂未收录或链接已调整，可返回事件档案继续浏览其他吃瓜证据合集。"
          keywords="131吃瓜,事件档案,证据合集"
          path="/events"
          type="website"
        />
        <SiteHeader />
        <main className="w-full bg-background-50 pt-28 md:pt-36 pb-20">
          <div className="max-w-[720px] mx-auto px-4 md:px-6 text-center">
            <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-lg bg-background-100 text-primary-500 text-3xl border border-background-200">
              <i className="ri-error-warning-line" />
            </span>
            <h1 className="mt-6 font-heading text-2xl md:text-3xl font-bold text-foreground-950">没有找到这条事件档案</h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              可能是链接已调整，或者该事件还未被收录。回到档案总览可以看到目前整理的全部证据合集。
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link
                to="/events"
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回事件档案
              </Link>
              <Link
                to="/"
                className="px-5 py-2.5 rounded-md border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                回到首页
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = events.filter((item) => item.categorySlug === event.categorySlug && item.id !== event.id).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: event.title,
      description: event.summary,
      inLanguage: "zh-CN",
      articleSection: event.category,
      dateModified: event.updatedAt,
      author: { "@type": "Organization", name: "131吃瓜" },
      publisher: { "@type": "Organization", name: "131吃瓜" },
      image: event.cover,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "事件档案", item: "/events" },
        { "@type": "ListItem", position: 3, name: event.title, item: `/event/${event.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${event.title} · 131吃瓜实锤证据合集`}
        description={`${event.title}：${event.summary.slice(0, 90)}...`}
        keywords={`131吃瓜,${event.title},实锤,证据合集,${event.tags.join(",")}`}
        path={`/event/${event.id}`}
        type="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100/40 pt-24 md:pt-32 pb-10 md:pb-14 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumbs
              items={[{ label: "首页", to: "/" }, { label: "事件档案", to: "/events" }, { label: event.title }]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2 md:gap-3">
              <span className="px-2.5 py-1 rounded-md text-xs md:text-sm bg-background-200/70 text-foreground-700">
                {event.category}
              </span>
              <span className={`px-2.5 py-1 rounded-md text-xs md:text-sm border ${statusStyleMap[event.status] ?? ""}`}>
                {event.status}
              </span>
              <span className="text-xs md:text-sm text-foreground-500">最后整理于 {formatDate(event.updatedAt)}</span>
            </div>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
              {event.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-lg leading-7 md:leading-8 text-foreground-500">{event.summary}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {event.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-100 border border-background-200 text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 lg:gap-12">
            <div>
              <div className="relative w-full h-64 sm:h-80 md:h-[420px] rounded-lg overflow-hidden border border-background-200">
                <img src={event.cover} alt={event.coverAlt} className="w-full h-full object-cover object-top" />
              </div>

              <nav aria-label="页内目录" className="mt-6 flex flex-wrap gap-3 text-sm">
                <a href="#evidence-list" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200 text-foreground-700 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                  <i className="ri-file-list-3-line" />
                  证据清单（{event.evidences.length} 条）
                </a>
                <a href="#timeline" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-200 text-foreground-700 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                  <i className="ri-timeline-view" />
                  事件时间线
                </a>
              </nav>

              <section id="evidence-list" className="scroll-mt-28 pt-10 md:pt-14">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-300">
                    <i className="ri-file-list-3-line" />
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">证据清单</h2>
                </div>
                <p className="mt-3 text-sm leading-7 text-foreground-500">
                  以下素材按收录顺序排列，标注了类型、来源与是否已核实。没有原始出处的条目会明确标为待核实。
                </p>
                <EvidenceList evidences={event.evidences} />
              </section>

              <section id="timeline" className="scroll-mt-28 pt-12 md:pt-16">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-300">
                    <i className="ri-timeline-view" />
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">事件时间线</h2>
                </div>
                <p className="mt-3 text-sm leading-7 text-foreground-500">
                  把分散素材按发生顺序排好，专门用来对照同一方在不同时间点的说法是否一致。
                </p>
                <TimelineSection timeline={event.timeline} />
              </section>
            </div>

            <EventSidebar event={event} related={related} />
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}